(function(w) {
  'use strict';

  // Check dependencies
  if (!Cnvs.Utils) cWarn('⚠️ Cnvs.State requires Cnvs.Utils');
  if (!Cnvs.Errors) cWarn('⚠️ Cnvs.State requires Cnvs.Errors');
  
  Cnvs.State = {
    FRAME_EDGES: ["Wrapped","White","Black"],

    IMAGE_STATUS: {
      PENDING: 'pending',
      UPLOADING: 'uploading', 
      UPLOADED: 'uploaded',
      ERROR: 'error'
    },

    UPLOAD_QUEUE_STATUS: {
      WAITING: 'waiting',
      PROCESSING: 'processing', 
      COMPLETED: 'completed',
      FAILED: 'failed'
    },

    DEVICE_TYPES: {
      MOBILE: 'M',
      DESKTOP: 'D'
    },

    EDIT_MODES: {
      NONE: 'none',
      CROP: 'crop',
      SIZE: 'size',
      STYLE: 'style',
      BORDER: 'border',
      ACRYLIC: 'acrylic'
    },
    
    data: null,
    db: null,
    fallback: false,

    autoSave: {
      enabled: true,
      debounceMs: 500,
      timeoutId: null
    },
    
    // Event listeners
    listeners: {
      'frame:added': [],
      'frame:removed': [],
      'frame:updated': [],
      'size:changed': [],
      'style:changed': [],
      'border:changed': [],
      'acrylic:changed': [],
      'loading:changed': [],
      'error:occurred': [],
      'state:restored': [],
      'state:saved': [],
      'state:loaded': [],
      'popup:opened': [],
      'popup:closed': [],
      'popup:updated': []
    },

    // State history for undo/redo
    history: {
      states: [],
      currentIndex: -1,
      maxStates: 20
    },
    
    // ================================================================
    // INITIALIZATION
    // ================================================================
    
    async initDB() {
        return Cnvs?.Errors?.safeAsync(async () => {
          
          if (this.db || this.isFallback || w.db) {
            cLog('ℹ️ Database already initialized');
            return this.db || w.db || null;
          }

          cLog('🗄️ Cnvs.State: Initializing IndexedDB');

          try {
            
            if (!('indexedDB' in window)) throw new Error('IndexedDB not supported');
            if (typeof w.idb?.openDB !== 'function') throw new Error('idb library not available');

            this.db = await w.idb.openDB('cnvsStateDB', 3, {
              upgrade(db, oldVersion, newVersion) {
                cLog(`🔄 Upgrading database v${oldVersion} → v${newVersion}`);
                if (!db.objectStoreNames.contains('appState')) {
                  const store = db.createObjectStore('appState', { keyPath: 'id' });
                  store.createIndex('timestamp', 'timestamp');
                  cLog('✅ Created appState store');
                }
              },
            });

            cLog('✅ IndexedDB initialized successfully');
          } catch (err) {
            // console.warn('⚠️ IndexedDB failed. ', err);
            this.isFallback = true;
          }

          return this.db || null;
        }, 'initDB', null);
      },

    async init(initialData = {}) {
      cLog('🏗️ Initializing Products Gallery State...');
      
      await this.initDB();
      const savedState = await this.loadFromDB();
      
      if (savedState) {
        cLog('📂 Loaded existing state');
        this._restoreUIFromState(savedState);
        this.data = savedState;
      } else {
        cLog('🆕 Creating new state');
        this.data = this._mergeState(this._getDefaultState(), initialData);
      }
      
      this._addToHistory(this.data);
      this.updateComputed();
      
      if (this.data.frames?.length > 0) {
        await this.rebuildDOMFromState();
      }

      // Fix any frames with filestack URLs but wrong status
      this.fixFrameUploadStatus();

      // Resume uploads if there are frames that need uploading
      setTimeout(() => {
        if (Cnvs.Images && typeof Cnvs.Images.resumeUploads === 'function') {
          Cnvs.Images.resumeUploads();
        }
      }, 2000); // Give some time for other modules to load
      
      cLog('✅ State initialized');
      this.triggerUpdate('state:loaded', { framesCount: this.data.frames.length });


      S$$(".dp_vp__size-box__item").forEach((sizeItem) => {
        const style = this.data.selectedStyle;
        const style4Search = style === 'None' ? 'Wrapped_Canvas' : style;
        const size = sizeItem.dataset.value.split('x');
        const sortedSize = parseInt(size[0]) < parseInt(size[1]) ? `${style4Search}__${size[0]}x${size[1]}` : `${style4Search}__${size[1]}x${size[0]}`;
        const frameData = Cnvs.Utils.findPByKey(sortedSize);
        sizeItem.querySelector("label:last-of-type").innerText = `$${frameData.price} Each`;
      });

      return this.data;
    },

    // ================================================================
    // PERSISTENCE
    // ================================================================
    
    async saveToDB() {
        return Cnvs?.Errors?.safeAsync(async () => {
          if (!this.data) return false;

          cLog('💾 Cnvs.State: Saving state');
          this.updateLoading('database', true);

          try {
            this._updateAllFrameImgStyles();
            this.data.timestamp = Date.now();
            const safeData = window.Cnvs.Utils.sanitizeAppStateForStorage(this.data);

            if (this.isFallback) {
              localStorage.setItem('pgAppState', JSON.stringify(safeData));
              cLog('💾 Saved state to localStorage');
            } else {
              const tx = this.db.transaction(['appState'], 'readwrite');
              await tx.objectStore('appState').put({ id: 'main', ...safeData });
              await tx.complete;
              cLog('💾 Saved state to IndexedDB');
            }

            this.updateLoading('database', false);
            this.clearError('database');
            
            // Re-enable ATC button after successful save (if cart criteria met)
            if (this.data.computed?.canAddToCart) {
              S$Q('.dp_st__atc-btn')?.classList.remove('disa');
            }
            
            this.triggerUpdate('state:saved', { timestamp: this.data.timestamp });
            return true;
          } catch (error) {
            console.error('❌ Failed to save state:', error);
            this.setError('database', `Save failed: ${error.message}`);
            this.updateLoading('database', false);
            
            // Re-enable ATC button even on save error (failsafe)
            if (this.data.computed?.canAddToCart) {
              S$Q('.dp_st__atc-btn')?.classList.remove('disa');
            }
            
            throw error;
          }
        }, 'saveToDB', false);
      },

    async loadFromDB() {
        return Cnvs?.Errors?.safeAsync(async () => {
          cLog('📂 Cnvs.State: Loading state');
          this.updateLoading('database', true);

          try {
            let savedState = null;

            if (this.isFallback) {
              const raw = localStorage.getItem('pgAppState');
              savedState = raw ? JSON.parse(raw) : null;
              cLog('📂 Loaded state from localStorage');
            } else {
              const tx = this.db.transaction(['appState'], 'readonly');
              savedState = await tx.objectStore('appState').get('main');
              cLog('📂 Loaded state from IndexedDB');
            }

            this.updateLoading('database', false);

            if (savedState) {
              this.clearError('database');
            }

            return savedState;
          } catch (error) {
            console.error('❌ Failed to load state:', error);
            this.setError('database', `Load failed: ${error.message}`);
            this.updateLoading('database', false);
            throw error;
          }
        }, 'loadFromDB', null);
      },
      
      async clearDB() {
        return Cnvs?.Errors?.safeAsync(async () => {
          if (this.isFallback) {
            localStorage.removeItem('pgAppState');
            cLog('🗑️ Cleared state from localStorage');
            return true;
          }

          const tx = this.db.transaction(['appState'], 'readwrite');
          await tx.objectStore('appState').clear();
          await tx.complete;
          cLog('🗑️ Cleared state from IndexedDB');
          return true;
        }, 'clearDB', false);
      },

    // ================================================================
    // DOM SYNCHRONIZATION
    // ================================================================
    
    async rebuildDOMFromState() {
      return Cnvs?.Errors?.safeAsync(async () => {
        if (!this.data?.frames?.length) return 0;
        
        cLog(`🔧 Rebuilding DOM (${this.data.frames.length} frames)`);
        
        const imgPreview = document.getElementById('img-preview');
        if (!imgPreview) return 0;
        
        // Clear existing frames (except upload container)
        document.querySelectorAll('.frame_container').forEach(container => {
          if (!container.querySelector('.img-uptile-container')) {
            container.remove();
          }
        });

        let rebuiltCount = 0;

        const framesToProcess = [...this.data.frames]; 

        for (const frame of framesToProcess) {
          try {
           if (frame.image) {
            try {
              if (frame.image.file instanceof Blob) {
                frame.image.previewURL = URL.createObjectURL(frame.image.file);
              } else if (typeof frame.image.filestackUrl === 'string' && frame.image.filestackUrl.startsWith('http')) {
                // frame.image.previewURL = frame.image.filestackUrl;
                  await new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => {
                      frame.image.previewURL = frame.image.filestackUrl;
                      resolve();
                    };
                    img.onerror = () => {
                      reject(new Error('Filestack URL image load failed'));
                    };
                    img.src = frame.image.filestackUrl;
                  });
              } else {
                throw new Error('No valid image file or Filestack URL');
              }
            } catch (error) {
             console.warn(`❌ Skipping frame ${frame.id} due to missing or broken image`, error);
             this.removeFrame(frame.id);
             continue;
            }
          }
            const frameHTML = w.Cnvs.Frames.frameHTML(frame);
            imgPreview.insertAdjacentHTML('beforeend', frameHTML);
            const frameContainer = document.querySelector(`.framefh[data-frame-id="${frame.id}"]`).closest('.frame_container');
            frameContainer?.classList.remove("frame_hidden");
            rebuiltCount++;
          } catch (error) {
            console.error(`❌ Error rebuilding frame ${frame.id}:`, error);
          }
        }
        
        this._updateUIAfterRebuild(rebuiltCount);
        
        cLog(`✅ Rebuilt ${rebuiltCount} frames`);
        return rebuiltCount;
      }, 'rebuildDOMFromState', 0) || 0;
    },

    // ================================================================
    // STATE MANAGEMENT
    // ================================================================
    
    getState() { return this.data ? this._deepClone(this.data) : null; },
    
    get(path, defaultValue = null) {
      if (!this.data) return defaultValue;
      const keys = path.split('.');
      let current = this.data;
      for (const key of keys) {
        if (current?.[key] === undefined) return defaultValue;
        current = current[key];
      }
      return current;
    },

    updateSelectedSize(width, height) {
      if (!this.data) return;
      
      const oldSize = this.data.selectedSize;
      const isLandscape = width > height;
      
      this.data.selectedSize = {
        variant: isLandscape ? `${height}x${width}` : `${width}x${height}`,
        size: `${width}x${height}`,
        width: parseInt(width),
        height: parseInt(height)
      };
      
      setTimeout(() => {
        const imgContainer = S$Q('.img-uptile-container');
        if (imgContainer) this.data.uploaderCss = imgContainer.getAttribute('style');
      }, 1000);
      
      this._updateAndSave('size:changed', { oldSize, newSize: this.data.selectedSize });
    },

    updateSelectedStyle(style) {
      if (!this.data) return;
      const oldStyle = this.data.selectedStyle;
      this.data.selectedStyle = style;
      this._updateAndSave('style:changed', { oldStyle, newStyle: style });
    },

    updateSelectedEdge(edge) {
      if (!this.data) return;
      const oldEdge = this.data.selectedEdge;
      this.data.selectedEdge = edge;
      this.data.selectedBorder = edge;
      this._updateAndSave('edge:changed', { oldEdge, newEdge: edge });
    },

    updateAcrylic(enabled) {
      if (!this.data) return;
      const oldAcrylic = this.data.withAcrylic;
      this.data.withAcrylic = Boolean(enabled);
      this._updateAndSave('acrylic:changed', { oldAcrylic, newAcrylic: enabled });
    },

    updateBW(enabled) {
      if (!this.data) return;
      const oldBW = this.data.withBW;
      this.data.withBW = Boolean(enabled);
      this._updateAndSave('bw:changed', { oldBW, newBW: enabled });
    },

    openPopup() {
      if (!this.data) return;
      
      // Delegate to the dedicated popup manager
      if (Cnvs.Popup) {
        Cnvs.Popup.open();
      } else {
        console.warn('⚠️ Cnvs.Popup not available');
      }
    },

    closePopup() {
      if (!this.data) return;
      
      // Delegate to the dedicated popup manager
      if (Cnvs.Popup) {
        Cnvs.Popup.close();
      } else {
        console.warn('⚠️ Cnvs.Popup not available');
      }
    },

    updatePopupState(updates = {}) {
      // Delegate to the dedicated popup manager
      if (Cnvs.Popup) {
        return Cnvs.Popup.update(updates);
      } else {
        console.warn('⚠️ Cnvs.Popup not available');
        return false;
      }
    },

    // ================================================================
    // FRAME MANAGEMENT
    // ================================================================
    
    addFrame(frameData) {
      if (!this.data) return null;
      
      return Cnvs?.Errors?.safe(() => {
        const frame = this._validateFrame(frameData);
        
        if (!frame) return null;
        
        // Debug: Log where this is being called from
        console.trace('🔍 Cnvs.State.addFrame called from:');
        cLog(`📝 Adding frame to state: ${frame.id} (Total frames: ${this.data.frames.length + 1})`);
        
        this.data.frames.push(frame);
        this._updateAndSave('frame:added', { frame });
        return frame;
      }, 'addFrame') || null;
    },

    // Get frame by ID
    getFrame(frameId) {
      if (!this.data?.frames) return null;
      return this.data.frames.find(f => f.id === frameId) || null;
    },

    // Get all frames
    getFrames() {
      return this.data?.frames || [];
    },

    removeFrame(frameId) {
      if (!this.data) return false;
      
      const frameIndex = this.data.frames.findIndex(f => f.id === frameId);
      if (frameIndex === -1) return false;
      
      const removedFrame = this.data.frames.splice(frameIndex, 1)[0];
      this._cleanupFrameImageBlob(removedFrame);
      
      this.reindexFrames();
      this._updateAndSave('frame:removed', { frame: removedFrame });
      return true;
    },

    updateFrame(frameId, updates) {
      if (!this.data) return false;
      
      const frame = this.data.frames.find(f => f.id === frameId);
      if (!frame) return false;
      
      const oldFrame = this._deepClone(frame);
      Object.assign(frame, updates);
      this._updateAndSave('frame:updated', { frameId, oldFrame, newFrame: frame });
      return true;
    },

    updateFrameImage(frameId, imageData) {
      if (!imageData || typeof imageData !== 'object') return false;
      
      // Ensure we have both file and previewURL when updating image
      const processedImageData = { ...imageData };
      
      // If file is provided but no previewURL, create one
      if (processedImageData.file && !processedImageData.previewURL) {
        try {
          processedImageData.previewURL = URL.createObjectURL(processedImageData.file);
          cLog('📷 Created preview URL for frame image update');
        } catch (error) {
          console.warn('⚠️ Failed to create preview URL:', error);
        }
      }
      
      // Update status to uploading if file is present
      if (processedImageData.file && !processedImageData.status) {
        processedImageData.status = this.IMAGE_STATUS.UPLOADING;
      }
      
      return this.updateFrame(frameId, { image: processedImageData });
    },

    // ================================================================
    // UPLOAD QUEUE MANAGEMENT
    // ================================================================

    addToUploadQueue(frameId) {
      if (!this.data) return false;
      
      // Check if already in queue
      const existingIndex = this.data.uploadQueue.findIndex(q => q.frameId === frameId);
      if (existingIndex !== -1) {
        cLog(`⚠️ Frame ${frameId} already in upload queue`);
        return false;
      }

      const queueItem = {
        frameId,
        status: this.UPLOAD_QUEUE_STATUS.WAITING,
        attempts: 0,
        maxAttempts: 3,
        addedAt: Date.now(),
        lastAttemptAt: null,
        error: null
      };

      this.data.uploadQueue.push(queueItem);
      this.updateComputed();
      this.scheduleSave();
      this.triggerUpdate('upload:queue-added', { frameId, queueLength: this.data.uploadQueue.length });
      
      cLog(`📤 Added frame ${frameId} to upload queue (${this.data.uploadQueue.length} items)`);
      return true;
    },

    updateUploadQueueItem(frameId, updates) {
      if (!this.data) return false;
      
      const queueItem = this.data.uploadQueue.find(q => q.frameId === frameId);
      if (!queueItem) return false;

      Object.assign(queueItem, updates);
      this.updateComputed();
      this.scheduleSave();
      this.triggerUpdate('upload:queue-updated', { frameId, updates });
      
      return true;
    },

    removeFromUploadQueue(frameId) {
      if (!this.data) return false;
      
      const queueIndex = this.data.uploadQueue.findIndex(q => q.frameId === frameId);
      if (queueIndex === -1) return false;

      this.data.uploadQueue.splice(queueIndex, 1);
      this.updateComputed();
      this.scheduleSave();
      this.triggerUpdate('upload:queue-removed', { frameId, queueLength: this.data.uploadQueue.length });
      
      cLog(`✅ Removed frame ${frameId} from upload queue (${this.data.uploadQueue.length} remaining)`);
      return true;
    },

    getUploadQueueStatus() {
      if (!this.data) return null;
      
      const queue = this.data.uploadQueue;
      const waiting = queue.filter(q => q.status === this.UPLOAD_QUEUE_STATUS.WAITING);
      const processing = queue.filter(q => q.status === this.UPLOAD_QUEUE_STATUS.PROCESSING);
      const completed = queue.filter(q => q.status === this.UPLOAD_QUEUE_STATUS.COMPLETED);
      const failed = queue.filter(q => q.status === this.UPLOAD_QUEUE_STATUS.FAILED);

      return {
        total: queue.length,
        waiting: waiting.length,
        processing: processing.length,
        completed: completed.length,
        failed: failed.length,
        isComplete: queue.length === 0 || completed.length === queue.length, // Empty queue is complete
        hasFailures: failed.length > 0,
        nextWaiting: waiting[0] || null
      };
    },

    clearUploadQueue() {
      if (!this.data) return false;
      
      this.data.uploadQueue = [];
      this.updateComputed();
      this.scheduleSave();
      this.triggerUpdate('upload:queue-cleared', {});
      
      cLog('🗑️ Upload queue cleared');
      return true;
    },

    // Get frames that need to be uploaded (have file but no filestackUrl)
    getFramesNeedingUpload() {
      if (!this.data?.frames) return [];
      
      return this.data.frames.filter(frame => 
        frame.image?.file && 
        !frame.image?.filestackUrl && 
        frame.image?.status !== this.IMAGE_STATUS.UPLOADED
      );
    },

    // Update frame upload status and manage queue
    updateFrameUploadStatus(frameId, status, filestackUrl = null, error = null) {
      const frame = this.getFrame(frameId);
      if (!frame) return false;

      const oldStatus = frame.image?.status;
      
      // Update frame image status
      const imageUpdate = {
        ...frame.image,
        status,
        modified: Date.now()
      };

      if (filestackUrl) {
        imageUpdate.filestackUrl = filestackUrl;
      }

      if (error) {
        imageUpdate.error = error;
      }

      this.updateFrame(frameId, { image: imageUpdate });

      // Update upload queue item
      const queueStatus = status === this.IMAGE_STATUS.UPLOADING ? this.UPLOAD_QUEUE_STATUS.PROCESSING
                        : status === this.IMAGE_STATUS.UPLOADED ? this.UPLOAD_QUEUE_STATUS.COMPLETED
                        : status === this.IMAGE_STATUS.ERROR ? this.UPLOAD_QUEUE_STATUS.FAILED
                        : this.UPLOAD_QUEUE_STATUS.WAITING;

      this.updateUploadQueueItem(frameId, {
        status: queueStatus,
        lastAttemptAt: Date.now(),
        error: error
      });

      // Remove from queue if completed or permanently failed
      if (status === this.IMAGE_STATUS.UPLOADED) {
        this.removeFromUploadQueue(frameId);
      }

      this.triggerUpdate('upload:status-changed', { 
        frameId, 
        oldStatus, 
        newStatus: status, 
        filestackUrl,
        error 
      });

      cLog(`📊 Frame ${frameId} upload status: ${oldStatus} → ${status}`);
      return true;
    },

    // ================================================================
    // STATE UTILITIES
    // ================================================================
    
    updateLoading(key, value) {
      if (this.data) {
        this.data.loading[key] = Boolean(value);
        this.triggerUpdate('loading:changed', { key, value });
      }
    },

    updateUI(key, value) {
      if (this.data) {
        this.data.ui[key] = value;
        this.triggerUpdate('ui:changed', { key, value });
      }
    },

    setError(type, error) {
      if (this.data) {
        this.data.errors[type] = error;
        this.triggerUpdate('error:occurred', { type, error });
      }
    },

    clearError(type) {
      if (this.data) {
        this.data.errors[type] = null;
        this.triggerUpdate('error:cleared', { type });
      }
    },

    generateFrameId() {
      const makeId = Cnvs?.Utils?.makeId || (() => Math.random().toString(36).substr(2, 8));
      return `frame_${Date.now()}_${makeId(6)}`;
    },

    reindexFrames() {
      this.data?.frames?.forEach((frame, index) => { frame.index = index; });
    },

    updateComputed() {
      if (!this.data) return;
      
      const frames = this.data.frames;
      const uploaded = frames.filter(f => f.image?.status === this.IMAGE_STATUS.UPLOADED);
      const uploading = frames.filter(f => f.image?.status === this.IMAGE_STATUS.UPLOADING);
      const failed = frames.filter(f => f.image?.status === this.IMAGE_STATUS.ERROR);
      const queueStatus = this.getUploadQueueStatus();
      
      // Debug logging for canAddToCart calculation
      const hasFrames = frames.length > 0;
      const allUploaded = uploaded.length === frames.length;
      const queueComplete = !queueStatus || queueStatus.isComplete;
      const canAddToCart = hasFrames && allUploaded && queueComplete;
      
      if (frames.length > 0) {
        cLog(`🔍 canAddToCart calculation:
          hasFrames: ${hasFrames} (${frames.length} frames)
          allUploaded: ${allUploaded} (${uploaded.length}/${frames.length})
          queueComplete: ${queueComplete} (queue: ${JSON.stringify(queueStatus)})
          result: ${canAddToCart}`);
          
        // Log frame statuses for debugging
        frames.forEach(f => {
          cLog(`📋 Frame ${f.id}: status=${f.image?.status}, hasFilestack=${!!f.image?.filestackUrl}`);
        });
      }
      
      this.data.computed = {
        totalPrice: frames.reduce((sum, f) => sum + (f.price || 0), 0),
        frameCount: frames.length,
        uploadProgress: { 
          completed: uploaded.length, 
          uploading: uploading.length,
          failed: failed.length,
          total: frames.length, 
          percentage: frames.length > 0 ? (uploaded.length / frames.length) * 100 : 0,
          isComplete: frames.length > 0 && uploaded.length === frames.length,
          hasUploading: uploading.length > 0,
          hasFailures: failed.length > 0
        },
        uploadQueue: queueStatus || { total: 0, waiting: 0, processing: 0, completed: 0, failed: 0, isComplete: true, hasFailures: false },
        hasFrames: frames.length > 0,
        canAddToCart: canAddToCart
      };
    },

    scheduleSave() {
      if (!this.autoSave.enabled) return;
      
      clearTimeout(this.autoSave.timeoutId);
      this.autoSave.timeoutId = setTimeout(() => {
        this.saveToDB().catch(error => console.error('❌ Auto-save failed:', error));
      }, this.autoSave.debounceMs);
    },

    // ================================================================
    // EVENT SYSTEM
    // ================================================================
    
    on(event, callback) {
      if (!this.listeners[event]) this.listeners[event] = [];
      this.listeners[event].push(callback);
      
      return () => {
        const index = this.listeners[event].indexOf(callback);
        if (index > -1) this.listeners[event].splice(index, 1);
      };
    },

    triggerUpdate(type, data = {}) {
      const event = { type, data, timestamp: Date.now(), state: this.getState() };
      
      this.listeners[type]?.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          Cnvs?.Errors?.handle(error, `state-event:${type}`, 'medium');
        }
      });
    },

    // ================================================================
    // PRIVATE HELPERS
    // ================================================================
    
    _getDefaultState() {
      const safeQuery = Cnvs?.Utils?.safeQuery || (s => {
        try { return document.querySelector(s); } catch(e) { return null; }
      });
      
      const selSize = safeQuery('.dp_vp__size-box__item.selected p')?.innerText?.trim() || '8x10';
      const selStyle = safeQuery('.dp_vp__frame-box__item.selected p')?.innerText?.trim() || 'Oak';
      const selBorder = safeQuery('.dp_vp__style-box__item.selected p')?.innerText?.trim() || 'White';
      const selAcrylic = safeQuery('#vpacr')?.checked || false;
      const selBW = safeQuery('#vpabw')?.checked || false;

      const [width, height] = selSize.split('x').map(Number);
      const isLandscape = width > height;
      
      return {
        id: 'main', version: '3.0', timestamp: Date.now(),
        selectedSize: {
          variant: isLandscape ? `${height}x${width}` : `${width}x${height}`,
          size: selSize, width, height
        },
        selectedStyle: selStyle, 
        selectedBorder: selBorder, 
        selectedEdge: selBorder, 
        withAcrylic: selAcrylic,
        withBW: selBW,
        selectedAcrylic: selAcrylic,
        frames: [], 
        uploadQueue: [],
        loading: { 
          frames: loadingStatus, 
          upload: false, 
          database: false, 
          initialization: false 
        },
        errors: { upload: null, database: null, validation: [], network: null },
                ui: {
          activeFrameId: null, editMode: this.EDIT_MODES.NONE, cropMode: false,
          sidebarOpen: true, deviceType: dType
        },
        // Note: Popup state (pu) moved to Cnvs.Popup for transient storage
        computed: { totalPrice: 0, frameCount: 0, uploadProgress: { completed: 0, total: 0 }, hasFrames: false, canAddToCart: false }
      };
    },

    _restoreUIFromState(state) {
      // Restore dropdown selections
      S$Q('.dp_vp__size-box')?.setAttribute('data-selected-size', state.selectedSize.size);
      S$Q('.dp_vp__style-box')?.setAttribute('data-selected-style', state.selectedStyle);
      S$Q('.dp_vp__edge-box')?.setAttribute('data-selected-edge', state.selectedEdge);

      // Update selected items
      const updateSelected = (container, value) => {
        container?.querySelector('.selected')?.classList.remove('selected');
        container?.querySelector(`[data-value="${value}"]`)?.classList.add('selected');
      };

      updateSelected(S$Q('.dp_vp__size-box'), state.selectedSize.size);
      updateSelected(S$Q('.dp_vp__style-box'), state.selectedStyle);
      updateSelected(S$Q('.dp_vp__edge-box'), state.selectedEdge);

      // Restore container attributes
      const imgContainer = S$Q('.img-uptile-container');
      if (imgContainer) {
        if (state.uploaderCss) imgContainer.setAttribute('style', state.uploaderCss);
        imgContainer.setAttribute('data-width-in-inches', state.selectedSize.width);
        imgContainer.setAttribute('data-height-in-inches', state.selectedSize.height);
        imgContainer.setAttribute('data-frame-style', state.selectedStyle);
        imgContainer.setAttribute('data-frame-edge', state.selectedEdge);
      }

      const acrylicCheckbox = S$G('vpacr');
      if (acrylicCheckbox) acrylicCheckbox.checked = state.withAcrylic;

      const bwCheckbox = S$G('vpabw');
      if (bwCheckbox) bwCheckbox.checked = state.withBW;
    },

    _setupFrameEventListeners() {
      const setupListener = (selector, eventType, handler) => {
        document.querySelectorAll(selector).forEach(el => el.addEventListener(eventType, handler));
      };

      setupListener('.dp_rb', 'click', (e) => {
        e.stopPropagation();
        const frameId = e.target.closest('.framefh')?.dataset.frameId;
        if (frameId) {
          this.removeFrame(frameId);
          e.target.closest('.frame_container')?.remove();
          Cnvs.Frames?.setFID?.();
          Cnvs.Frames?.updateFrameSizes?.("size");
          this.scheduleSave();
          if (this.data.frames.length === 0) {
            S$Q('.dp_st__atc-btn')?.classList.add('disa');
          }
        }
      });

      setupListener('.dp_eb', 'click', w.editpu);
      
      setupListener('.framefh', 'click', (e) => {
        if (!e.target.closest('.dp_eb, .dp_cb, .dp_rb, .dp_lr')) {
          w.editpu?.(e);
        }
      });

      setupListener('.dp_cb', 'click', w.editpu);
    },

    _updateUIAfterRebuild(rebuiltCount) {
      if (rebuiltCount > 0) {
        S$Q('#MainContent')?.setAttribute('data-fa', 'true');
        S$Q('.dp_st__atc-btn')?.classList.remove('disa');

        Cnvs.Frames?.handleSwiper?.(this.data.frames);
        Cnvs.Frames?.setFID?.();
        Cnvs.Frames?.updateFrameSizes?.("PageLoad");
      }
    },

    _validateFrame(frame) {
      if (!frame || typeof frame !== 'object') return null;
      
      // Create default frame structure
      const defaultFrame = {
        id: this.generateFrameId(), 
        index: this.data.frames.length,
        style: this.data.selectedStyle, 
        border: this.data.selectedBorder,
        size: {
          variant: this.data.selectedSize.variant, 
          size: this.data.selectedSize.size,
          width: this.data.selectedSize.width,
          height: this.data.selectedSize.height
        },
        acrylic: this.data.withAcrylic, 
        price: 0,
        image: {
          file: null, 
          previewURL: null, 
          filestackUrl: null, 
          status: this.IMAGE_STATUS.PENDING,
          crop: null, 
          visableCrop: null, 
          scale: 'min', 
          imgStyles: '', 
          manualChange: false
        },
        bw: this.data.withBW,
        cssStyles: '', 
        position: { x: 0, y: 0 }, 
        rotation: 0,
        created: Date.now(), 
        modified: Date.now()
      };
      
      // Merge with provided frame data
      const validatedFrame = { ...defaultFrame, ...frame };
      return validatedFrame;
    },

    _updateAndSave(eventType, eventData) {
      this.updateComputed();
      this._addToHistory(this.data);
      this.scheduleSave();
      this.triggerUpdate(eventType, eventData);
    },

    _deepClone(obj) {
      return Cnvs?.Utils?.clone?.(obj) || JSON.parse(JSON.stringify(obj));
    },

    _mergeState(defaultState, overrides) {
      return Cnvs?.Utils?.merge?.({}, defaultState, overrides) || { ...defaultState, ...overrides };
    },

    _addToHistory(state) {
      const clonedState = this._deepClone(state);
      
      if (this.history.currentIndex < this.history.states.length - 1) {
        this.history.states = this.history.states.slice(0, this.history.currentIndex + 1);
      }
      
      this.history.states.push(clonedState);
      this.history.currentIndex++;
      
      if (this.history.states.length > this.history.maxStates) {
        this.history.states.shift();
        this.history.currentIndex--;
      }
    },

    // Clean up blob URLs to prevent memory leaks
    _cleanupFrameImageBlob(frame) {
      if (frame?.image?.previewURL && frame.image.previewURL.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(frame.image.previewURL);
          cLog('🗑️ Cleaned up blob URL for removed frame');
        } catch (error) {
          console.warn('⚠️ Failed to cleanup blob URL:', error);
        }
      }
    },

    // Clean up all blob URLs (useful for cleanup on page unload)
    _cleanupAllBlobs() {
      if (!this.data?.frames) return;
      
      this.data.frames.forEach(frame => {
        this._cleanupFrameImageBlob(frame);
      });
      cLog('🗑️ Cleaned up all blob URLs');
    },

    // Update imgStyles for all frames from DOM
    _updateAllFrameImgStyles() {
      if (!this.data?.frames) return;
      
      this.data.frames.forEach(frame => {
        try {
          // Find the image element for this frame
          const frameElement = document.querySelector(`[data-frame-id="${frame.id}"]`);
          if (frameElement) {
            const imgElement = frameElement.querySelector('img');
            if (imgElement) {
              const currentStyles = imgElement.getAttribute('style') || '';
              if (frame.image) {
                frame.image.imgStyles = currentStyles;
                cLog(`🎨 Updated imgStyles for frame ${frame.id}: ${currentStyles}`);
              }
            }
          }
        } catch (error) {
          console.warn(`⚠️ Failed to update imgStyles for frame ${frame.id}:`, error);
        }
      });
    },

    // Update imgStyles for a specific frame from DOM
    updateFrameImgStyles(frameId) {
      if (!this.data?.frames || !frameId) return false;
      
      const frame = this.getFrame(frameId);
      if (!frame) return false;
      
      try {
        const frameElement = document.querySelector(`[data-frame-id="${frameId}"]`);
        if (frameElement) {
          const imgElement = frameElement.querySelector('img');
          if (imgElement) {
            const currentStyles = imgElement.getAttribute('style') || '';
            if (frame.image) {
              frame.image.imgStyles = currentStyles;
              cLog(`🎨 Updated imgStyles for frame ${frameId}: ${currentStyles}`);
              return true;
            }
          }
        }
      } catch (error) {
        console.warn(`⚠️ Failed to update imgStyles for frame ${frameId}:`, error);
      }
      
      return false;
    },

    // ================================================================
    // STATUS & DEBUG
    // ================================================================
    
    isDBAvailable() { return Boolean(this.db); },

    getDBStatus() {
      return {
        available: this.isDBAvailable(),
        autoSaveEnabled: this.autoSave.enabled,
        storage: 'Unified IndexedDB'
      };
    },

    getSettingsStatus() {
      return this.data ? {
        size: this.data.selectedSize.variant,
        style: this.data.selectedStyle,
        border: this.data.selectedBorder,
        acrylic: this.data.withAcrylic,
        bw: this.data.withBW,
        autoSave: this.autoSave.enabled,
        lastSaved: this.data.timestamp ? new Date(this.data.timestamp).toISOString() : null
      } : null;
    },

    // Debug helper to fix frames with filestack URLs but wrong status
    fixFrameUploadStatus() {
      if (!this.data?.frames) return 0;
      
      let fixedCount = 0;
      this.data.frames.forEach(frame => {
        if (frame.image?.filestackUrl && frame.image?.status !== this.IMAGE_STATUS.UPLOADED) {
          cLog(`🔧 Fixing upload status for frame ${frame.id}: ${frame.image.status} → uploaded`);
          frame.image.status = this.IMAGE_STATUS.UPLOADED;
          fixedCount++;
        }
      });
      
      if (fixedCount > 0) {
        this.updateComputed();
        this.scheduleSave();
        this.triggerUpdate('upload:status-fixed', { fixedCount });
        cLog(`✅ Fixed ${fixedCount} frame(s) upload status`);
      }
      
      return fixedCount;
    },

    // Debug helper to check current state
    debugUploadState() {
      if (!this.data) {
        console.warn('⚠️ No state data available');
        return;
      }

      console.group('🔍 Upload State Debug');
      console.log('📋 Frames:', this.data.frames.map(f => ({
        id: f.id,
        hasFile: !!f.image?.file,
        hasFilestackUrl: !!f.image?.filestackUrl,
        status: f.image?.status,
        needsUpload: !!f.image?.file && !f.image?.filestackUrl
      })));
      console.log('📤 Upload Queue:', this.data.uploadQueue);
      console.log('📊 Computed:', this.data.computed);
      console.log('🎯 Can Add to Cart:', this.data.computed?.canAddToCart);
      console.groupEnd();
    },

    logInfo() {
      console.groupCollapsed('📦 Cnvs.State module loaded - State Management Features');
      console.table({
        'State Management': ['Centralized store', 'Reactive updates', 'Event system', 'History'],
        'Frame Management': ['Add/Remove frames', 'Image handling', 'Blob URL management', 'File + preview URLs'],
        'Persistence': ['Unified IndexedDB', 'Auto-save', 'Settings included'],
        'UI Integration': ['DOM sync', 'Size handling', 'Frame generation']
      });
      console.table({ 'Current Status': this.getDBStatus() });
      console.groupEnd();
    }
  };

  // Auto-initialize
  Cnvs.State.init().catch(error => console.error('❌ Failed to initialize Cnvs.State:', error));

  // Setup cleanup on page unload to prevent memory leaks
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      Cnvs.State._cleanupAllBlobs();
    });
  }

  // Mark module as loaded
  if (Cnvs.moduleStatus) {
    Cnvs.moduleStatus.state = true;
    Cnvs.checkModules?.();
  }

  // Auto-log info
  if (typeof w !== 'undefined' && !Cnvs._silentLoad) {
    Cnvs.State.logInfo();
  }

  // Expose debug functions globally for manual testing
  if (typeof w !== 'undefined') {
    w.debugUploadState = () => Cnvs.State.debugUploadState();
    w.fixFrameUploadStatus = () => Cnvs.State.fixFrameUploadStatus();
    w.updateComputed = () => { Cnvs.State.updateComputed(); return Cnvs.State.data.computed; };
    w.checkImageStatuses = () => {
      const frames = Cnvs.State.data.frames;
      console.log('📊 Image Status Constants:', Cnvs.State.IMAGE_STATUS);
      console.log('📋 Frame Statuses:', frames.map(f => ({ 
        id: f.id, 
        status: f.image?.status,
        statusType: typeof f.image?.status,
        isUploaded: f.image?.status === Cnvs.State.IMAGE_STATUS.UPLOADED,
        hasFilestack: !!f.image?.filestackUrl
      })));
    };
    w.fixUploadIssues = () => {
      cLog('🔧 Fixing all upload issues...');
      // Clear upload queue for uploaded frames
      Cnvs.State.data.uploadQueue = Cnvs.State.data.uploadQueue.filter(q => {
        const frame = Cnvs.State.getFrame(q.frameId);
        return !(frame?.image?.filestackUrl && frame?.image?.status === Cnvs.State.IMAGE_STATUS.UPLOADED);
      });
      // Fix frame statuses
      Cnvs.State.fixFrameUploadStatus();
      // Update computed state
      Cnvs.State.updateComputed();
      cLog('✅ Upload issues fixed, updated computed state:', Cnvs.State.data.computed);
      return Cnvs.State.data.computed;
    };
  }
  
  cLog('✅ Cnvs.State: Enhanced state management with image handling loaded');

})(window); 


/**
   * Represents a single frame and its state within the application.
   * Used for rendering, uploading, restoring from IndexedDB, and user customization.
   * @typedef {Object} Frame
   * @property {string} id - Unique internal frame ID (e.g. UUID)
   * @property {string} style - Frame style key (e.g. 'Modern Black')
   * @property {string} border - Border type (e.g. 'No Mat', 'Standard')
   * @property {Object} size - size object
   * @property {string} size.variant - Display size as in the variant small number first (e.g. '8x10', '10x12')
   * @property {string} size.size - Display size as in the UI (e.g. '8x10', '10x8')
   * @property {number} size.width - Display width in inches (e.g. '8')
   * @property {number} size.height - Display height in inches (e.g. '10')
   * @property {string} acrylic - Acrylic overlay option ('yes'|'no')
   * @property {number} price - Price of the configured frame
   * @property {number} variantID - Shopify variant ID or internal reference
   * @property {boolean} dpiWarning - Warning flag or DPI comment if resolution is low
   * @property {number} positionIndex - Order index for UI sorting and reordering
   * @property {Object} cssStyles - CSS styles for the frame
   * @property {number} cssStyles.height - Height in pixels
   * @property {number} cssStyles.width - Width in pixels
   * @property {number} cssStyles.borderWidth - Border width in pixels
   * @property {Object} image - Image data
   * @property {Object} image.file - Original uploaded file (may be null after upload)
   * @property {string} image.previewURL - Local preview URL (created via URL.createObjectURL or blob)
   * @property {string} image.filestackUrl - Permanent Filestack URL after successful upload
   * @property {'pending'|'uploading'|'uploaded'|'error'} image.status - Upload lifecycle status
   * @property {Object} image.crop - Crop data
   * @property {Object} image.visableCrop - Visable crop data
   * @property {number} image.scale - Scale of the image
   * @property {string} image.imgStyles - Image styles
   * @property {boolean} image.manualChange - Manual change flag
   * @property {boolean} image.minScale - Minimum scale flag
   */ 